/**
 * useBaselineAssignments.ts
 * 6C.6 Compare-to-Baseline ghost layer: fetches each task's BASELINE (no-scenario)
 * assignments so the grids can render "baseline: N%" sublabels under the scenario
 * bars. Only runs when compareToBaseline is ON. Shared by the Classic + Board task
 * grids so the ghost layer is identical in both.
 */

import { useEffect, useState } from 'react';
import { fetchAssignmentsByTask } from '../../../../services/scheduleAssignments';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';

/** Map of ScheduleTaskKey → baseline assignee rows. */
export type BaselineAssignmentMap = Record<string, TaskAssignee[]>;

export function useBaselineAssignments(
  tasks: TaskWithBookings[],
  compareToBaseline: boolean | undefined,
): BaselineAssignmentMap {
  // Intentionally loads the BASELINE (non-scenario) assignments for comparison — a
  // separate, read-only layer, NOT a desync risk with the scenario assignment map.
  const [map, setMap] = useState<BaselineAssignmentMap>({});

  useEffect(() => {
    if (!compareToBaseline || tasks.length === 0) {
      setMap({});
      return;
    }
    const controller = new AbortController();
    Promise.all(
      tasks.map(async (t) => {
        try {
          const rows = await fetchAssignmentsByTask(t.ScheduleTaskKey);
          return [t.ScheduleTaskKey, rows] as const;
        } catch {
          return [t.ScheduleTaskKey, [] as TaskAssignee[]] as const;
        }
      }),
    ).then((results) => {
      if (controller.signal.aborted) return;
      const next: BaselineAssignmentMap = {};
      for (const [key, rows] of results) next[key] = rows;
      setMap(next);
    }).catch(() => {
      // Best-effort — ghost layer stays empty on error.
    });
    return () => { controller.abort(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareToBaseline, tasks]);

  return map;
}
